import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, BatteryChargingIcon, RefreshCwIcon } from 'lucide-react';
import { rotationSteps } from '../../data/mountSystem';
import { useLanguage } from '../../contexts/LanguageContext';
import { bundleName, formatMoney, getBundle, priceBundle } from '../../utils/pricing';
import { useShopNavigation } from '../../hooks/useShopNavigation';
import { track } from '../../utils/analytics';
import { buttonClasses } from '../../utils/buttonStyles';
type LightSet = 'A' | 'B';

/** Four fixed base positions on the pool walls (SVG coordinates). */
const POSITIONS = [{
  x: 44,
  y: 92
}, {
  x: 44,
  y: 168
}, {
  x: 356,
  y: 92
}, {
  x: 356,
  y: 168
}];

/** 8-Light Signature System weekly rotation: 4 installed + 4 charged, swapped onto the same bases. */
export function RotationSystem({
  showOffer = true
}: {
  showOffer?: boolean;
}) {
  const {
    lang
  } = useLanguage();
  const reduce = useReducedMotion();
  const shop = useShopNavigation();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    amount: 0.4
  });
  const [installed, setInstalled] = useState<LightSet>('A');
  const [auto, setAuto] = useState(true);
  useEffect(() => {
    if (!auto || reduce || !inView) return;
    const id = window.setInterval(() => setInstalled(s => s === 'A' ? 'B' : 'A'), 3600);
    return () => window.clearInterval(id);
  }, [auto, reduce, inView]);
  const charging: LightSet = installed === 'A' ? 'B' : 'A';
  const bundle = getBundle('sys8');
  const p = priceBundle(bundle);
  const m = (n: number) => formatMoney(n, lang);
  const swap = () => {
    setAuto(false);
    setInstalled(charging);
  };
  return <div ref={ref} className="grid gap-10 lg:grid-cols-[1.15fr_1fr] lg:gap-16" data-id="element-562" data-mp="3d53rk7faaqry">
      <div data-id="element-563" data-mp="eir8d6katcjb">
        <figure className="mf-hud border border-marine/25 bg-[#06121F] p-4 md:p-6" data-id="element-564" data-mp="ej99ohltigy3">
          <svg viewBox="0 0 400 260" className="w-full" role="img" aria-labelledby="rotation-title" data-id="element-565" data-mp="9owlju9echg0">
            <title id="rotation-title" data-id="element-566" data-mp="23z3i055ju46i">
              {`Pool plan with four fixed magnetic bases. Set ${installed} is installed and lighting the pool; set ${charging} is charging.`}
            </title>
            <rect x="44" y="40" width="312" height="180" rx="18" fill="#0B2A4A" stroke="#9CD8FF" strokeOpacity="0.5" data-id="element-567" data-mp="1u88kg4muri5r" />
            <rect x="44" y="40" width="312" height="180" rx="18" fill="#248DFF" opacity="0.14" data-id="element-568" data-mp="1z58qwgnm6mxe" />
            {POSITIONS.map((pos, i) => <g key={i} data-id="element-569" data-mp="1p9nsstlo26sx" data-mpi={i}>
                <circle cx={pos.x} cy={pos.y} r="15" fill="#E7ECF1" data-id="element-570" data-mp="1payxqwqulklm" />
                <motion.g key={`${installed}-${i}`} initial={reduce ? false : {
              opacity: 0,
              scale: 0.96
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.25,
              delay: reduce ? 0 : i * 0.05,
              ease: [0.23, 1, 0.32, 1]
            }} style={{
              transformOrigin: `${pos.x}px ${pos.y}px`
            }} data-id="element-571" data-mp="1baft7zllcwo7">
                  <circle cx={pos.x} cy={pos.y} r="11" fill="#F4F7FA" stroke="#248DFF" strokeWidth="2.5" data-id="element-572" data-mp="yyvir06lz650" />
                  <text x={pos.x} y={pos.y + 4} textAnchor="middle" fontSize="10" fontFamily="Barlow Condensed" fontWeight="700" fill="#0B2037" data-id="element-573" data-mp="u13gbc8c81mk">
                    {installed}
                    {i + 1}
                  </text>
                </motion.g>
              </g>)}
            <text x="200" y="136" textAnchor="middle" fill="#9CD8FF" fontSize="13" fontFamily="Barlow Condensed" fontWeight="600" letterSpacing="2" data-id="element-574" data-mp="1hpd76skerfkk">
              4 FIXED POSITIONS
            </text>
            <text x="200" y="244" textAnchor="middle" fill="#B8C6D3" fontSize="11" fontFamily="Inter" data-id="element-575" data-mp="1rolx9ac5ka1n">
              Bases never move — only the lights change
            </text>
          </svg>
        </figure>

        <div className="mt-4 flex flex-col gap-3 border border-white/10 bg-[#06121F] p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-576" data-mp="3teno96qyntsv">
          <div className="flex items-center gap-3" data-id="element-577" data-mp="3oi6x03ogszbk">
            <BatteryChargingIcon className="h-5 w-5 shrink-0 text-ice" aria-hidden data-id="element-578" data-mp="2fjxhb2p6rk2p" />
            <div data-id="element-579" data-mp="2ki8pl8zu1mit">
              <p className="font-display text-sm font-semibold uppercase tracking-[0.1em] text-white" data-id="element-580" data-mp="2khpfufkc64fv">Charging & ready</p>
              <ul className="mt-1 flex gap-1.5" aria-label={`Set ${charging} charging`} data-id="element-581" data-mp="2piwm2a6tyvkf">
                {[1, 2, 3, 4].map(n => <li key={n} className="inline-flex h-7 w-9 items-center justify-center rounded-full border border-ice/40 font-display text-xs font-bold text-ice" data-id="element-582" data-mp="2tgud7ik5vrqg">
                    {charging}
                    {n}
                  </li>)}
              </ul>
            </div>
          </div>
          <button type="button" onClick={swap} className={`${buttonClasses('secondary')} h-11 shrink-0`} data-id="element-583" data-mp="2alvtk4gtewkw">
            <RefreshCwIcon className="h-4 w-4" aria-hidden data-id="element-584" data-mp="1i3i6mzx0ov0l" /> Swap the sets
          </button>
        </div>
        <p className="sr-only" aria-live="polite" data-id="element-585" data-mp="1al0sgge74s1a">
          Set {installed} installed. Set {charging} charging.
        </p>
      </div>

      <div className="flex flex-col" data-id="element-586" data-mp="15lxlurikidr7">
        <ol className="space-y-5" data-id="element-587" data-mp="11jstrx7rpp6o">
          {rotationSteps.map((s, i) => <li key={i} className="grid grid-cols-[2rem_1fr] gap-3" data-id="element-588" data-mp="11lx7t7zds4ko" data-mpi={i}>
              <span className="font-display text-lg font-semibold text-marine" data-id="element-589" data-mp="wjy5m1fdndpb">{i + 1}</span>
              <p className="text-titanium" data-id="element-590" data-mp="rmerr2mld2xw">{s}</p>
            </li>)}
        </ol>
        <p className="mt-8 font-display text-2xl font-semibold uppercase tracking-[0.04em] text-ice md:text-3xl" data-id="element-591" data-mp="mtvey7vqabu5">
          No remounting. No guesswork. Just light.
        </p>

        {showOffer && <div className="mf-hud mt-8 border border-marine bg-[#0D2440] p-6" data-id="element-592" data-mp="dv9lvismref4">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-marine" data-id="element-593" data-mp="8voovdx0tg51">Most popular</p>
          <p className="mt-1 font-display text-2xl font-semibold uppercase tracking-[0.03em] text-white" data-id="element-594" data-mp="3viu3ovgfdiq">{bundleName(bundle, lang)}</p>
          <div className="mt-4 flex flex-wrap items-baseline gap-x-4 gap-y-1" data-id="element-595" data-mp="3qc9eb90unefq">
            <span className="font-display text-5xl font-semibold text-white" data-id="element-596" data-mp="3vdypqprzhu4c">{m(p.total)}</span>
            {p.savings > 0 && <span className="text-sm font-semibold uppercase tracking-[0.08em] text-ice" data-id="element-597" data-mp="3ggy6lanuz7hh">Save {m(p.savings)}</span>}
          </div>
          <p className="mt-1 text-sm text-titanium" data-id="element-598" data-mp="3lgkmxsyzh3ui">
            {m(p.perUnit)} per light · 8 M1 PRO lights for 4 permanent positions
          </p>
          <button type="button" onClick={() => {
          track('mount_rotation_cta', {
            bundleId: 'sys8',
            value: p.total
          });
          shop('sys8');
        }} className={`${buttonClasses('primary', 'lg')} mt-6 w-full`} data-id="element-599" data-mp="hyammjkwuf81">
            Choose the 8-Light System <ArrowRightIcon className="h-4 w-4" aria-hidden data-id="element-600" data-mp="vwqy0ntpwtwi" />
          </button>
        </div>}
      </div>
    </div>;
}